import React, { useEffect, useMemo } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  Container,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { audienceTypeOptions, audienceUserTypeOptions } from '@luxuryescapes/contract-svc-promo/audience';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import { useGetAudienceAdminList } from '~/hooks/apis/promo/useGetAudienceAdminList';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import {
  CreateAudienceAdminPayload,
  GetAudienceAdminListResponse,
  createAudienceAdmin,
  updateAudienceAdmin,
} from '~/services/PromoService';
import { getDayJs } from '~/services/TimeService';

import { audienceAdminQueryKeys } from '../../hooks/apis/promo/audienceAdminQueryKeys';

import AudienceSummary from './AudienceSummary';

const audienceSyncModelOptions = [
  { key: 'manual', title: 'Manual' },
  { key: 'syncIntoAudienceUser', title: 'Save as Audience User' },
] as const;

type AudienceAdminRecord = GetAudienceAdminListResponse['result']['audienceList'][number];

type AudienceFormValues = {
  audienceName: string;
  enabled: boolean;
  syncModel: CreateAudienceAdminPayload['syncModel'];
  audienceType: CreateAudienceAdminPayload['audienceType'];
  audienceOverrideExpiresAt: string;
  audienceOverrideExpiresAtEnabled: boolean;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  userType: NonNullable<CreateAudienceAdminPayload['config']['userType']> | '';
};

type AudienceWritePayload = CreateAudienceAdminPayload & {
  brand: string;
};

type AudienceUserType = NonNullable<CreateAudienceAdminPayload['config']['userType']>;
type CreateAudienceAdminResponse = Awaited<ReturnType<typeof createAudienceAdmin>>;

function splitList(values?: Array<string>): string {
  return values?.join(', ') ?? '';
}

export function toList(value: string): Array<string> | undefined {
  const items = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  return items.length > 0 ? items : undefined;
}

function toAudienceUserType(value: string): AudienceUserType | undefined {
  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return undefined;
  }

  return audienceUserTypeOptions.find((option) => option.key === normalizedValue)?.key;
}

function toDefaultValues(audience?: AudienceAdminRecord): AudienceFormValues {
  const config = audience?.config;
  return {
    audienceName: audience?.audienceName ?? '',
    enabled: audience?.enabled ?? true,
    syncModel: audience?.syncModel ?? 'manual',
    audienceType: audience?.audienceType ?? 'staticList',
    audienceOverrideExpiresAt: audience?.audienceOverrideExpiresAt ?? '',
    audienceOverrideExpiresAtEnabled: Boolean(audience?.audienceOverrideExpiresAt),
    utmSource: splitList(config?.utm?.source),
    utmMedium: splitList(config?.utm?.medium),
    utmCampaign: splitList(config?.utm?.campaign),
    utmTerm: splitList(config?.utm?.term),
    userType: config?.userType ?? '',
  };
}

export function buildPayload(values: AudienceFormValues, brand: string): AudienceWritePayload {
  return {
    brand,
    audienceName: values.audienceName.trim(),
    audienceType: values.audienceType,
    syncModel: values.syncModel,
    enabled: values.enabled,
    audienceOverrideExpiresAt: values.audienceOverrideExpiresAtEnabled
      ? values.audienceOverrideExpiresAt || null
      : null,
    config: {
      utm: {
        source: toList(values.utmSource),
        medium: toList(values.utmMedium),
        campaign: toList(values.utmCampaign),
        term: toList(values.utmTerm),
      },
      userType: toAudienceUserType(values.userType),
    },
  };
}

export function getCreatedAudienceId(response: CreateAudienceAdminResponse): string | undefined {
  const audienceId = response.result?.audience?.audienceId;

  return typeof audienceId === 'string' && audienceId.trim() ? audienceId : undefined;
}

export default function AudienceAdminPage() {
  const params = useParams<{ audienceId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const brand = useCurrentBrand();

  const audienceId = params.audienceId;
  const isEdit = location.pathname.endsWith('/edit');
  const isCreate = !audienceId;

  const audienceListQuery = useGetAudienceAdminList({
    query: { brand, enabled: 'all', page: 0, pageSize: 500, sortOrder: 'asc' },
    enabled: !isCreate,
  });

  const audience = useMemo(
    () => audienceListQuery.data?.result.audienceList.find((item) => item.audienceId === audienceId),
    [audienceId, audienceListQuery.data],
  );

  const { control, handleSubmit, reset, setValue, clearErrors } = useForm<AudienceFormValues>({
    defaultValues: toDefaultValues(),
  });

  const audienceOverrideExpiresAtEnabled = useWatch({ control, name: 'audienceOverrideExpiresAtEnabled' });

  useEffect(() => {
    if (isCreate) {
      reset(toDefaultValues());
      return;
    }

    if (audience) {
      reset(toDefaultValues(audience));
    }
  }, [audience, isCreate, reset]);

  useEffect(() => {
    if (!audienceOverrideExpiresAtEnabled) {
      setValue('audienceOverrideExpiresAt', '');
      clearErrors('audienceOverrideExpiresAt');
    }
  }, [audienceOverrideExpiresAtEnabled, clearErrors, setValue]);

  const createMutation = useMutation({
    mutationFn: async (values: AudienceFormValues) => createAudienceAdmin(buildPayload(values, brand)),
    onSuccess: async (response) => {
      const createdAudienceId = getCreatedAudienceId(response);

      if (!createdAudienceId) {
        enqueueSnackbar('Audience was created, but the API did not return an audience ID.', { variant: 'error' });
        return;
      }

      await queryClient.invalidateQueries({ queryKey: audienceAdminQueryKeys.all });
      enqueueSnackbar('Audience created', { variant: 'success' });
      navigate(`/marketing/audiences/${createdAudienceId}`);
    },
    onError: (mutationError) => {
      enqueueSnackbar(mutationError instanceof Error ? mutationError.message : 'Failed to create audience', {
        variant: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (values: AudienceFormValues) => {
      if (!audienceId) {
        throw new Error('Missing audience id');
      }
      return updateAudienceAdmin(audienceId, buildPayload(values, brand));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: audienceAdminQueryKeys.all });
      enqueueSnackbar('Audience updated', { variant: 'success' });
      navigate(`/marketing/audiences/${audienceId}`);
    },
    onError: (mutationError) => {
      enqueueSnackbar(mutationError instanceof Error ? mutationError.message : 'Failed to update audience', {
        variant: 'error',
      });
    },
  });

  const onSubmit = (values: AudienceFormValues) => {
    if (isEdit) {
      updateMutation.mutate(values);
      return;
    }

    createMutation.mutate(values);
  };

  if (!isCreate && audienceListQuery.isLoading) {
    return <Spinner />;
  }

  if (!isCreate && audienceListQuery.isError) {
    return (
      <Container maxWidth="xl">
        <PageHeader title="Audience" backButton="/marketing/audiences" />
        <Alert severity="error">
          {audienceListQuery.error instanceof Error ? audienceListQuery.error.message : 'Failed to load audience'}
        </Alert>
      </Container>
    );
  }

  if (!isCreate && !audience) {
    return (
      <Container maxWidth="xl">
        <PageHeader title="Audience" backButton="/marketing/audiences" />
        <Alert severity="warning">Audience not found.</Alert>
      </Container>
    );
  }

  const pageTitle = isCreate
    ? 'Create Audience'
    : isEdit
      ? `Edit Audience: ${audience?.audienceName}`
      : audience?.audienceName;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title={pageTitle} backButton="/marketing/audiences" />

        {!isCreate && !isEdit ? (
          <Stack spacing={3} sx={{ mt: 3 }}>
            <AudienceSummary audience={audience} />
            <Button component={Link} to={`/marketing/audiences/${audienceId}/edit`} variant="contained">
              Edit audience
            </Button>
          </Stack>
        ) : (
          <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                <Controller
                  control={control}
                  name="audienceName"
                  rules={{ required: 'Audience name is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Audience name"
                      fullWidth
                      required
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Stack spacing={2}>
                  <Controller
                    control={control}
                    name="audienceOverrideExpiresAtEnabled"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(event) => field.onChange(event.target.checked)}
                            onBlur={field.onBlur}
                            inputRef={field.ref}
                          />
                        }
                        label="Add Audience End Date"
                      />
                    )}
                  />
                  {audienceOverrideExpiresAtEnabled ? (
                    <Controller
                      control={control}
                      name="audienceOverrideExpiresAt"
                      rules={{
                        validate: (value) => {
                          if (!audienceOverrideExpiresAtEnabled) {
                            return true;
                          }
                          const trimmed = value.trim();
                          if (!trimmed) {
                            return 'Please enter an audience end date, or turn off the toggle.';
                          }
                          if (!getDayJs(trimmed).isValid()) {
                            return 'Please enter a valid date and time.';
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <DateTimeWidget
                          value={field.value}
                          onChange={field.onChange}
                          label="Audience override expires at"
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  ) : null}
                </Stack>
                <Box component={Paper} variant="outlined" sx={{ p: 3 }}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="h6">Audience config</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Settings that define the audience type and how it is maintained.
                      </Typography>
                    </Box>
                    <Controller
                      control={control}
                      name="audienceType"
                      render={({ field }) => (
                        <TextField {...field} label="Audience type" fullWidth select>
                          {audienceTypeOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <Controller
                      control={control}
                      name="syncModel"
                      render={({ field }) => (
                        <TextField {...field} label="Sync model" fullWidth select>
                          {audienceSyncModelOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <Controller
                      control={control}
                      name="enabled"
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />
                          }
                          label="Enabled"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="userType"
                      render={({ field }) => (
                        <TextField {...field} label="User type" helperText="Optional" fullWidth select>
                          <MenuItem value="">
                            <Typography component="span" sx={{ fontStyle: 'italic' }}>
                              None
                            </Typography>
                          </MenuItem>
                          {audienceUserTypeOptions.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Stack>
                </Box>
                <Box component={Paper} variant="outlined" sx={{ p: 3 }}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="h6">UTM targeting</Typography>
                      <Typography variant="body2" color="text.secondary">
                        When a user lands on the website with these utm params set, they will be considered a member of
                        this audience.
                      </Typography>
                    </Box>
                    <Controller
                      control={control}
                      name="utmSource"
                      render={({ field }) => (
                        <TextField {...field} label="UTM source values" helperText="Comma-separated values" fullWidth />
                      )}
                    />
                    <Controller
                      control={control}
                      name="utmMedium"
                      render={({ field }) => (
                        <TextField {...field} label="UTM medium values" helperText="Comma-separated values" fullWidth />
                      )}
                    />
                    <Controller
                      control={control}
                      name="utmCampaign"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="UTM campaign values"
                          helperText="Comma-separated values"
                          fullWidth
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="utmTerm"
                      render={({ field }) => (
                        <TextField {...field} label="UTM term values" helperText="Comma-separated values" fullWidth />
                      )}
                    />
                  </Stack>
                </Box>
                <Stack direction="row" spacing={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {isEdit ? 'Save changes' : 'Create audience'}
                  </Button>
                  <Button component={Link} to="/marketing/audiences" variant="text">
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            </form>
          </Paper>
        )}
      </Container>
    </>
  );
}
